import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Paper, useMediaQuery } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useLocation } from 'react-router-dom';
import Calendar from '../common/Calendar';
import TimeHeatmap from '../common/TimeHeatmap';
import DateTimeHeatmap from '../common/DateTimeHeatmap';
import TodayDetails from '../common/TodayDetails';
import EventEffect from '../common/EventEffect';
import YearlyTrendGrid from '../trend/YearlyTrendGrid';
import MonthlyTrendGrid from '../trend/MonthlyTrendGrid';
import WeeklyTrendGrid from '../trend/WeeklyTrendGrid';
import ForeignersRanking from '../common/ForeignersRanking';
import ForeignersDistribution from '../common/ForeignersDistribution';
import ForeignersYearlyDistribution from '../common/ForeignersYearlyDistribution';
import SectionContainer from '../ui/SectionContainer';
import { useDashboard } from '../../contexts/DashboardContext';
import theme from '../../theme/theme';
import { detectLanguageFromPathname } from '../../i18n';

const FOREIGNERS_ALLOWED_YEARS = ['2023', '2024'];

/**
 * VisualizationRouter - アクションに応じた可視化コンポーネントを表示
 */
function VisualizationRouter({ action, data, loading, variant }) {
    const isMobile = useMediaQuery('(max-width:768px)');
    const location = useLocation();
    const isEnglish = detectLanguageFromPathname(location.pathname) === 'en';
    const { year: selectedYear, month: selectedMonth } = useDashboard();
    
    // 外国人データの年・月チェック
    const isForeignersYearValid = selectedYear && FOREIGNERS_ALLOWED_YEARS.includes(selectedYear);
    const needsForeignersDateSelection = action === 'foreigners_distribution' && (!isForeignersYearValid || !selectedMonth);
    const needsForeignersYearSelection = action === 'foreigners_yearly_distribution' && !isForeignersYearValid;
    
    // 外国人データ選択プロンプト
    const renderForeignersPrompt = (message) => (
        <SectionContainer>
            <Paper
                elevation={1}
                sx={{
                    borderRadius: '12px',
                    p: isMobile ? 2 : 3,
                    textAlign: 'center',
                    backgroundColor: 'rgba(21, 101, 192, 0.04)',
                    border: '1px dashed rgba(21, 101, 192, 0.4)',
                }}
            >
                <ArrowUpwardIcon 
                    sx={{ 
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        color: theme.palette.primary.main,
                        mb: 1
                    }} 
                />
                <Typography 
                    variant="h6" 
                    sx={{ 
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        mb: 1,
                        fontSize: isMobile ? '1rem' : '1.1rem'
                    }}
                >
                    {message}
                </Typography>
            </Paper>
        </SectionContainer>
    );
    
    // 注: prediction_calendar アクションは撤廃済み。
    // 当月以降の年/月選択時は cal_*/dti_* のレスポンスに予測がオーバーレイされる。

    // 今日について詳しく知りたい
    if (action === 'today_details') {
        return <TodayDetails />;
    }
    
    // イベント効果分析
    if (action === 'event_effect') {
        return <EventEffect />;
    }
    
    // 外国人分布（月別）
    if (action === 'foreigners_distribution') {
        if (needsForeignersDateSelection) {
            return renderForeignersPrompt(isEnglish ? 'Select year and month to display' : '表示したい年月を選んでください');
        }
        return <ForeignersDistribution />;
    }
    
    // 外国人分布（年別）
    if (action === 'foreigners_yearly_distribution') {
        if (needsForeignersYearSelection) {
            return renderForeignersPrompt(isEnglish ? 'Select year to display' : '表示したい年を選んでください');
        }
        return <ForeignersYearlyDistribution />;
    }
    
    // 傾向分析系
    if (action === 'year_trend') {
        return (
            <SectionContainer>
                <YearlyTrendGrid data={data || []} loading={loading} isMobile={isMobile} />
            </SectionContainer>
        );
    }
    
    if (action === 'month_trend') {
        return (
            <SectionContainer>
                <MonthlyTrendGrid data={data || []} loading={loading} isMobile={isMobile} />
            </SectionContainer>
        );
    }
    
    if (action === 'week_trend') {
        return (
            <SectionContainer>
                <WeeklyTrendGrid data={data || []} loading={loading} isMobile={isMobile} />
            </SectionContainer>
        );
    }
    
    // グラフベースのビジュアライゼーション（全て表示）
    // カレンダー・ヒートマップ系は条件によって表示を切り替え
    return (
        <>
            {/* グラフが表示される場合のみランキングを表示 */}
            {(action?.startsWith('cal_') || action?.startsWith('wti_') || action?.startsWith('dti_')) && (
                <ForeignersRanking />
            )}
            
            {/* カレンダー系のアクション */}
            {action?.startsWith('cal_') && <Calendar />}
            
            {/* 時間系のアクション */}
            {action?.startsWith('wti_') && <TimeHeatmap />}
            
            {/* 日時系のアクション */}
            {action?.startsWith('dti_') && <DateTimeHeatmap />}
        </>
    );
}

VisualizationRouter.propTypes = {
    action: PropTypes.string,
    data: PropTypes.any,
    loading: PropTypes.bool,
    variant: PropTypes.oneOf(['purpose', 'function']),
};

export default VisualizationRouter;
